import React, { useState, useRef, useEffect } from "react";
import { Button } from "@mhome/ui";

const Button1Widget = ({ widget, onValueUpdate }) => {
  const [isLongPress, setIsLongPress] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [clickType, setClickType] = useState(null);
  const [isPressed, setIsPressed] = useState(false);
  const longPressTimer = useRef(null);
  const longPressDelay = 500;
  const mouseDownTime = useRef(null);
  const mouseDownPosition = useRef(null);

  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, []);

  const handleValueUpdate = async (type) => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      setClickType(type);

      const updateData = {
        id: widget.id,
        value: type === "long" ? "long click" : "click",
      };

      await onValueUpdate(updateData);
    } catch (error) {
      console.error("Failed to update value:", error);
    } finally {
      setTimeout(() => {
        setIsProcessing(false);
        setClickType(null);
      }, 300);
    }
  };

  const startLongPress = (e) => {
    if (isProcessing) return;

    // Store the initial mouse position and time
    mouseDownPosition.current = { x: e.clientX, y: e.clientY };
    mouseDownTime.current = Date.now();
    setIsPressed(true);

    longPressTimer.current = setTimeout(() => {
      setIsLongPress(true);
      handleValueUpdate("long");
    }, longPressDelay);
  };

  const endLongPress = (e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    // Check if this was a real click (not just a hover)
    if (!isLongPress && !isProcessing && mouseDownPosition.current) {
      // Calculate distance moved
      const distance = Math.sqrt(
        Math.pow(e.clientX - mouseDownPosition.current.x, 2) +
          Math.pow(e.clientY - mouseDownPosition.current.y, 2)
      );

      // Only trigger if mouse hasn't moved much (less than 5px)
      if (distance < 5) {
        handleValueUpdate("click");
      }
    }

    setIsLongPress(false);
    setIsPressed(false);
    mouseDownPosition.current = null;
    mouseDownTime.current = null;
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  // Determine button style based on state
  const getButtonStyle = () => {
    let style = {
      height: "100%",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      touchAction: "none",
      transition: "all 0.2s ease",
      boxShadow: "none",
      textTransform: "none",
    };

    if (isPressed) {
      style.transform = "scale(0.98)";
      style.boxShadow = "0 1px 3px rgba(0,0,0,0.2)";
    }

    if (clickType === "long") {
      style.backgroundColor = "#1976d2"; // Darker blue for long press
      style.boxShadow = "0 0 0 2px rgba(25, 118, 210, 0.5)";
    } else if (clickType === "click") {
      style.backgroundColor = "#2196f3"; // Standard blue for regular click
    }

    return style;
  };

  return (
    <div
      className="flex items-stretch h-full w-full"
      style={{ touchAction: "none" }}
    >
      {widget && (
        <Button
          variant="contained"
          onMouseDown={startLongPress}
          onMouseUp={endLongPress}
          onMouseLeave={endLongPress}
          onTouchStart={startLongPress}
          onTouchEnd={endLongPress}
          onContextMenu={handleContextMenu}
          style={getButtonStyle()}
        >
          {widget.label}
          {clickType && (
            <div
              className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center text-white text-xs font-bold transition-all rounded shadow-none"
              style={{
                backgroundColor: "hsl(var(--secondary))",
              }}
            >
              {clickType === "long" ? "LONG CLICKED" : "CLICKED"}
            </div>
          )}
        </Button>
      )}
    </div>
  );
};

export default Button1Widget;
